import { useTranslation } from "react-i18next";
import type { ProjectTypeId } from "@features/Login/types";
import { Button } from "@components/Button";
import { parseHtml } from "@components/HTMLParser/utils";
import { Modal } from "@components/Modal/Modal";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useDeleteProjectDataMutation } from "@services/jointBillServiceSettings/mutations";
import { useProjectDataQuery } from "@services/jointBillServiceSettings/queries";

interface IProjectDataInformationModalProps extends IUseModalProps {
  jointBillServiceId: number;
  projectTypeId: ProjectTypeId;
  onSubmit: () => void;
}

export const ProjectDataInformationModal: React.FC<
  IProjectDataInformationModalProps
> = ({ jointBillServiceId, projectTypeId, onSubmit, ...modalProps }) => {
  const { t } = useTranslation();
  const modal = useModal(modalProps);

  const { data: projectData } = useProjectDataQuery(jointBillServiceId);
  const { mutateAsync: deleteProjectData, isPending } =
    useDeleteProjectDataMutation(jointBillServiceId);

  const closeModal = async () => {
    if (!projectData || projectData.isOwner) {
      modal.close();
      return;
    } else {
      await deleteProjectData().then(modal.close);
    }
  };

  return (
    <Modal
      modal={modal}
      title={t(
        `settings.house.projectDataRenewal.information.title.${
          projectData?.isOwner ? "owner" : "payer"
        }`,
      )}
      actionButtons={
        <>
          <Button
            variant="secondary"
            className="w-full"
            onClick={closeModal}
            isLoading={isPending}
            aria-label={t(
              `settings.house.projectDataRenewal.information.buttons.cancel.${
                projectData?.isOwner ? "owner" : "payer"
              }`,
            )}
          >
            {t(
              `settings.house.projectDataRenewal.information.buttons.cancel.${
                projectData?.isOwner ? "owner" : "payer"
              }`,
            )}
          </Button>
          <Button
            className="w-full"
            onClick={() => {
              modal.close();
              onSubmit();
            }}
            isLoading={isPending}
            aria-label={t(
              "settings.house.projectDataRenewal.information.buttons.submit",
            )}
          >
            {t("settings.house.projectDataRenewal.information.buttons.submit")}
          </Button>
        </>
      }
    >
      <p>
        {parseHtml(
          t(
            `settings.house.projectDataRenewal.information.${projectTypeId}.description.${
              projectData?.isOwner ? "owner" : "payer"
            }`,
            { link: t(`externalProject.${projectTypeId}.rulesLink`) },
          ),
        )}
      </p>
    </Modal>
  );
};
